/**
 * Native Media3 köprüsü.
 *
 * Web uygulamasındaki tek `<audio>` öğesinin yerini Capacitor APK'sında alır.
 * Tarayıcı/PWA yolunda bu modül hiçbir native çağrı yapmaz; mevcut HTMLAudio
 * davranışı aynen kalır.
 */
import { Capacitor, type PluginListenerHandle, registerPlugin } from '@capacitor/core';
import type { Item } from './api.ts';
import { mediaArtworkUrl } from './mediaToken.ts';

export interface NativePlaybackState {
  isPlaying: boolean;
  isBuffering: boolean;
  positionMs: number;
  durationMs: number;
}

export interface NativeQueueItem {
  itemId: string;
  url: string;
  progressToken: string;
  artworkUrl: string | null;
  title: string;
  artist: string | null;
  album: string | null;
  durationMs: number | null;
}

interface PbforkAudioPlugin {
  loadMedia(options: {
    itemId: string;
    url: string;
    title: string;
    artist: string;
    album: string;
    artworkUrl: string | null;
    progressToken?: string;
    positionMs: number;
    autoplay: boolean;
  }): Promise<void>;
  loadQueue(options: {
    items: NativeQueueItem[];
    startIndex: number;
    positionMs: number;
    autoplay: boolean;
    shuffle: boolean;
    repeat: 'off' | 'all' | 'one';
  }): Promise<void>;
  /**
   * Kuyruktan tek öğeyi çıkarır.
   *
   * Kuyruğu **yeniden kurmaz**; çalan parça sökülmez, ses kesilmez.
   */
  removeQueueItem(options: { index: number; itemId: string }): Promise<void>;
  /** Kuyrukta bir öğeyi taşır (oynatma kesilmez). */
  moveQueueItem(options: { fromIndex: number; toIndex: number }): Promise<void>;
  /** Kuyruğa tek öğe ekler (oynatma kesilmez). */
  addQueueItem(options: { index: number; item: NativeQueueItem }): Promise<void>;
  play(): Promise<void>;
  pause(): Promise<void>;
  next(): Promise<void>;
  previous(): Promise<void>;
  seekTo(options: { positionMs: number }): Promise<void>;
  setVolume(options: { volume: number }): Promise<void>;
  setRate(options: { rate: number }): Promise<void>;
  setShuffle(options: { enabled: boolean }): Promise<void>;
  setRepeat(options: { mode: 'off' | 'all' | 'one' }): Promise<void>;
  stop(): Promise<void>;
  getState(): Promise<NativePlaybackState>;
  addListener(
    eventName: 'state' | 'trackChanged' | 'error' | 'ended',
    listener: (
      payload: NativePlaybackState & {
        error?: string;
        itemId?: string;
        index?: number;
      },
    ) => void,
  ): Promise<PluginListenerHandle>;
}

export const isNativeAudio = Capacitor.isNativePlatform();

const plugin: PbforkAudioPlugin | null = isNativeAudio
  ? registerPlugin<PbforkAudioPlugin>('PbforkAudio')
  : null;

/**
 * `HTMLAudioElement` yüzeyinin native oynatıcıya bağlanan küçük bir uyarlaması.
 *
 * `useAudioPlayer` mevcut HTML olaylarını ve alanlarını kullandığı için burada
 * yalnızca gerçekten ihtiyaç duyulan alt küme uygulanır; DOM öğesi oluşturulmaz.
 */
class NativeAudioElement extends EventTarget {
  src = '';
  duration = 0;
  paused = true;
  ended = false;
  private currentTimeValue = 0;
  private volumeValue = 1;
  private mutedValue = false;
  private rateValue = 1;
  private buffering = false;
  /**
   * Media3 `seekTo` asenkron; hedefe ulaşmadan gelen eski state web'i geri
   * çekmemeli. Bu alan, hedef doğrulanana kadar eski konumu yok sayar.
   */
  private pendingSeek: { targetMs: number; requestedAt: number } | null = null;

  private static readonly SEEK_TOLERANCE_MS = 1500;
  private static readonly SEEK_TIMEOUT_MS = 4000;

  get currentTime(): number {
    return this.currentTimeValue;
  }

  set currentTime(value: number) {
    const targetMs = Math.round(Math.max(0, value) * 1000);
    this.currentTimeValue = targetMs / 1000;
    this.pendingSeek = { targetMs, requestedAt: Date.now() };
    void this.nativePlugin.seekTo({ positionMs: targetMs }).catch(() => undefined);
  }

  get volume(): number {
    return this.volumeValue;
  }

  set volume(value: number) {
    this.volumeValue = Math.max(0, Math.min(1, value));
    void this.nativePlugin
      .setVolume({ volume: this.mutedValue ? 0 : this.volumeValue })
      .catch(() => undefined);
  }

  get muted(): boolean {
    return this.mutedValue;
  }

  set muted(value: boolean) {
    this.mutedValue = value;
    void this.nativePlugin
      .setVolume({ volume: value ? 0 : this.volumeValue })
      .catch(() => undefined);
  }

  get playbackRate(): number {
    return this.rateValue;
  }

  set playbackRate(value: number) {
    this.rateValue = value;
    void this.nativePlugin.setRate({ rate: value }).catch(() => undefined);
  }

  constructor(private readonly nativePlugin: PbforkAudioPlugin) {
    super();
    void nativePlugin.addListener('state', (state) => this.applyState(state));
    void nativePlugin.addListener('trackChanged', (state) => this.applyTrackChange(state));
    void nativePlugin.addListener('ended', () => {
      this.paused = true;
      this.ended = true;
      this.emit('pause');
      this.emit('ended');
    });
    void nativePlugin.addListener('error', () => this.emit('error'));
  }

  loadQueue(
    items: NativeQueueItem[],
    startIndex: number,
    positionMs: number,
    shuffle: boolean,
    repeat: 'off' | 'all' | 'one',
    autoplay = true,
  ): void {
    const current = items[startIndex];
    this.src = current?.url ?? '';
    this.pendingSeek = null;
    this.currentTimeValue = positionMs / 1000;
    this.duration = (current?.durationMs ?? 0) / 1000;
    this.ended = false;
    this.paused = !autoplay;
    this.emit('loadedmetadata');
    void this.nativePlugin
      .loadQueue({
        items,
        startIndex,
        positionMs,
        autoplay,
        shuffle,
        repeat,
      })
      .catch(() => this.emit('error'));
  }

  loadItem(item: Item, url: string, positionMs: number, progressToken?: string): void {
    this.src = url;
    this.pendingSeek = null;
    this.currentTimeValue = positionMs / 1000;
    this.duration = (item.durationMs ?? 0) / 1000;
    this.ended = false;
    this.paused = false;
    this.emit('loadedmetadata');
    void this.nativePlugin
      .loadMedia({
        itemId: item.id,
        url,
        title: item.title,
        artist: item.music?.artist ?? 'pbfork',
        album: item.music?.album ?? '',
        artworkUrl: mediaArtworkUrl(item.albumCoverUrl ?? item.posterUrl ?? item.thumbnailUrl),
        ...(progressToken === undefined ? {} : { progressToken }),
        positionMs,
        autoplay: true,
      })
      .catch(() => this.emit('error'));
  }

  load(): void {
    // Native yükleme `loadItem` içinde yapılır.
  }

  play(): Promise<void> {
    this.paused = false;
    this.ended = false;
    this.emit('play');
    return this.nativePlugin.play().catch(() => {
      this.paused = true;
      this.emit('pause');
    });
  }

  pause(): void {
    if (this.paused) return;
    this.paused = true;
    this.emit('pause');
    void this.nativePlugin.pause().catch(() => undefined);
  }

  next(): void {
    void this.nativePlugin.next().catch(() => undefined);
  }

  previous(): void {
    void this.nativePlugin.previous().catch(() => undefined);
  }

  /**
   * Kuyruktan tek öğeyi çıkarır (oynatma sürer).
   */
  removeQueueItem(index: number, itemId: string): void {
    void this.nativePlugin.removeQueueItem({ index, itemId }).catch(() => undefined);
  }

  /**
   * Kuyrukta bir öğeyi taşır (oynatma sürer).
   */
  moveQueueItem(fromIndex: number, toIndex: number): void {
    void this.nativePlugin.moveQueueItem({ fromIndex, toIndex }).catch(() => undefined);
  }

  /**
   * Kuyruğa tek öğe ekler (oynatma sürer).
   */
  addQueueItem(item: NativeQueueItem, index: number): void {
    void this.nativePlugin.addQueueItem({ index, item }).catch(() => undefined);
  }

  setShuffle(enabled: boolean): void {
    void this.nativePlugin.setShuffle({ enabled }).catch(() => undefined);
  }
  setRepeat(mode: 'off' | 'all' | 'one'): void {
    void this.nativePlugin.setRepeat({ mode }).catch(() => undefined);
  }

  removeAttribute(name: string): void {
    if (name === 'src') {
      this.src = '';
      this.pendingSeek = null;
      void this.nativePlugin.stop().catch(() => undefined);
    }
  }

  private applyTrackChange(state: NativePlaybackState & { itemId?: string; index?: number }): void {
    if (typeof state.itemId === 'string' && typeof state.index === 'number') {
      this.dispatchEvent(
        new CustomEvent('native-track', {
          detail: { itemId: state.itemId, index: state.index },
        }),
      );
    }
    this.applyState(state);
  }

  private applyState(state: NativePlaybackState): void {
    const wasPlaying = !this.paused;
    const previousDuration = this.duration;
    let statePositionMs = state.positionMs;

    if (this.pendingSeek !== null) {
      const ageMs = Date.now() - this.pendingSeek.requestedAt;
      const distance = Math.abs(state.positionMs - this.pendingSeek.targetMs);
      if (distance <= NativeAudioElement.SEEK_TOLERANCE_MS) {
        this.pendingSeek = null;
      } else if (ageMs < NativeAudioElement.SEEK_TIMEOUT_MS) {
        // Seek henüz uygulanmadı: eski Media3 konumu hedefin önüne geçmesin.
        statePositionMs = this.pendingSeek.targetMs;
      } else {
        // Controller seek'i reddettiyse kullanıcıyı sahte konumda bırakma.
        this.pendingSeek = null;
      }
    }

    this.paused = !state.isPlaying;
    this.currentTimeValue = statePositionMs / 1000;
    if (state.durationMs > 0) this.duration = state.durationMs / 1000;
    if (state.isBuffering !== this.buffering) {
      this.buffering = state.isBuffering;
      this.emit(state.isBuffering ? 'waiting' : 'playing');
    }
    if (previousDuration === 0 && this.duration > 0) this.emit('loadedmetadata');
    if (!wasPlaying && state.isPlaying) this.emit('play');
    if (wasPlaying && !state.isPlaying) this.emit('pause');
    this.emit('timeupdate');
  }

  private emit(name: string): void {
    this.dispatchEvent(new Event(name));
  }
}

let nativeElement: NativeAudioElement | null = null;

/** Uygulama ömrü boyunca tek native oynatıcı. */
export function getNativeAudioElement(): HTMLAudioElement | null {
  if (plugin === null) return null;
  nativeElement ??= new NativeAudioElement(plugin);
  return nativeElement as unknown as HTMLAudioElement;
}

/** Native servise yeni parça yükler; web yolunda no-op. */
export function loadNativeAudio(
  item: Item,
  url: string,
  positionMs: number,
  progressToken?: string,
): void {
  if (nativeElement !== null) nativeElement.loadItem(item, url, positionMs, progressToken);
}

/** Native Media3 kuyruğunu (ve varsa başlangıç konumunu) yükler. */
export function loadNativeQueue(
  items: NativeQueueItem[],
  startIndex: number,
  positionMs: number,
  shuffle: boolean,
  repeat: 'off' | 'all' | 'one',
  autoplay = true,
): void {
  if (nativeElement !== null) {
    nativeElement.loadQueue(items, startIndex, positionMs, shuffle, repeat, autoplay);
  }
}

export function nativeNext(): void {
  nativeElement?.next();
}

export function nativePrevious(): void {
  nativeElement?.previous();
}

export function nativeSetShuffle(enabled: boolean): void {
  nativeElement?.setShuffle(enabled);
}

/** Kuyruktan tek öğeyi çıkarır (oynatma kesilmez). */
export function nativeRemoveQueueItem(index: number, itemId: string): void {
  nativeElement?.removeQueueItem(index, itemId);
}

/** Kuyrukta bir öğeyi taşır (oynatma kesilmez). */
export function nativeMoveQueueItem(fromIndex: number, toIndex: number): void {
  nativeElement?.moveQueueItem(fromIndex, toIndex);
}

/** Kuyruğa tek öğe ekler (oynatma kesilmez). */
export function nativeAddQueueItem(item: NativeQueueItem, index: number): void {
  nativeElement?.addQueueItem(item, index);
}

export function nativeSetRepeat(mode: 'off' | 'all' | 'one'): void {
  nativeElement?.setRepeat(mode);
}
